import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore, useThemeStore } from '@/store';
import { darkTheme, lightTheme } from '@/theme';
import { AppLayout, YandexMetrika } from '@/components';
import { routesConfig } from '@/routes.config';
import { loginPath } from '@/utils/loginRedirect';
import { NotificationProvider } from '@/contexts/NotificationContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode } = useThemeStore();
  const theme = mode === 'dark' ? darkTheme : lightTheme;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to={loginPath(`${location.pathname}${location.search}`)} replace />;
  }
  return <>{children}</>;
};

function wrapElement(element: React.ReactNode, isPublic: boolean, layout: boolean, bare?: boolean): React.ReactNode {
  let wrapped = element;
  if (!isPublic) wrapped = <ProtectedRoute>{wrapped}</ProtectedRoute>;
  if (layout) wrapped = <AppLayout bare={bare}>{wrapped}</AppLayout>;
  return wrapped;
}

const AppContent: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <Routes>
      {routesConfig.map(({ path, element, public: isPublic, layout, bare }) => (
        <Route
          key={path}
          path={path}
          element={wrapElement(element, isPublic, layout, bare)}
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Box>
);

function App() {
  useEffect(() => {
    const handleNumberWheel = (event: WheelEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'number') {
        return;
      }

      // Prevent accidental value changes when user scrolls over focused number input.
      event.preventDefault();
      target.blur();
    };

    document.addEventListener('wheel', handleNumberWheel, { capture: true, passive: false });
    return () => document.removeEventListener('wheel', handleNumberWheel, true);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <NotificationProvider>
        <AppThemeProvider>
          <BrowserRouter>
            <YandexMetrika />
            <AppContent />
          </BrowserRouter>
        </AppThemeProvider>
      </NotificationProvider>
    </QueryClientProvider>
  );
}

export default App;
